/**
 * simulatedExportProgress — time-driven progress for the export-modal bar.
 *
 * Export is a single opaque await: the client fires ONE batch call
 * (exportToJira/Linear/Asana/Trello/Notion) that resolves only once the server
 * has exported every issue (`runExport` loops all tickets, _export-core.ts).
 * There is no per-issue feedback, so the old bar sat at 0% the whole time and
 * jumped to 100% in the same tick the modal flipped to the success view — the
 * 100% frame never painted and the bar looked dead for every provider.
 *
 * Instead, the in-progress view animates the bar on a timer: it eases
 * asymptotically from 0 toward a 90% ceiling, fast at first and slowing as it
 * approaches. It only reaches 100% when the export actually finishes (the modal
 * swaps to the success/error state and this unmounts).
 *
 * Same shape as the shared `ui/simulatedVerifyProgress.ts` (the verify card hit
 * the identical "one opaque await, frozen bar" problem). Export has no coarse
 * milestones, so this variant eases from a fixed 0 base instead of a floor.
 */
import { useEffect, useRef, useState } from 'react';
/** The timer alone never passes this; 100% means the export actually finished. */
export const SIMULATED_EXPORT_PROGRESS_CEILING = 90;
/** Easing time constant: ~63% of the remaining gap closes per TAU. */
const TAU_MS = 8_000;
/** UI update cadence. */
const TICK_MS = 200;
/**
 * Pure curve: percent shown `elapsedMs` after the export started. Monotonic in
 * elapsed time, clamped to [0, ceiling].
 */
export function simulatedExportPercent(elapsedMs) {
    const elapsed = Math.max(elapsedMs, 0);
    const eased = SIMULATED_EXPORT_PROGRESS_CEILING * (1 - Math.exp(-elapsed / TAU_MS));
    return Math.min(SIMULATED_EXPORT_PROGRESS_CEILING, Math.round(eased));
}
/**
 * Hook driving the displayed percent while an export is in flight.
 *
 * Mount it in a component that only exists during the 'exporting' state so each
 * run starts fresh at 0. The returned value eases up over time and never
 * decreases. The modal shows 100% by leaving this view on completion, not by
 * this hook.
 */
export function useSimulatedExportProgress() {
    const [displayed, setDisplayed] = useState(0);
    const startRef = useRef(Date.now());
    useEffect(() => {
        const id = setInterval(() => {
            setDisplayed((prev) => Math.max(prev, simulatedExportPercent(Date.now() - startRef.current)));
        }, TICK_MS);
        return () => clearInterval(id);
    }, []);
    return displayed;
}
//# sourceMappingURL=simulatedExportProgress.js.map